/**
 * Server auth errors → translated messages.
 *
 * The native auth endpoints (public/api/auth-native-*.php) answer failures with
 * a stable machine-readable `error` code alongside a human `message`. The
 * message is written in English by PHP and nothing translates it, so rendering
 * it directly would put an English sentence in the middle of a French modal.
 *
 * So the code is the thing we translate against, and the server's message
 * becomes the fallback for a code this table doesn't know yet — an English
 * sentence that is *specific* beats a translated one that is generic, and a new
 * server error code should degrade rather than disappear. `npm run i18n:check`
 * will flag the key if the translation is missing; nothing flags a code we
 * never mapped, which is why the fallback chain has to stay honest.
 *
 * Keep in sync with the `'error' => '…'` values in public/api/auth-native-*.php.
 */

import { tr } from '../i18n/standalone';

/** Every native-auth error code that has its own translated message. */
const ERROR_KEYS: Record<string, string> = {
  account_disabled: 'auth:errors.accountDisabled',
  email_exists: 'auth:errors.emailExists',
  email_failed: 'auth:errors.emailFailed',
  email_unavailable: 'auth:errors.emailUnavailable',
  expired: 'auth:errors.codeExpired',
  incorrect: 'auth:errors.codeIncorrect',
  invalid: 'auth:errors.codeIncorrect',
  invalid_credentials: 'auth:errors.invalidCredentials',
  invalid_input: 'auth:errors.invalidInput',
  no_account: 'auth:errors.noAccount',
  no_pending: 'auth:errors.startAgain',
  not_verified: 'auth:errors.notVerified',
  password_update_failed: 'auth:errors.passwordUpdateFailed',
  rate_limited: 'auth:errors.rateLimited',
  reset_failed: 'auth:errors.resetFailed',
  staff_domain: 'auth:errors.staffManaged',
  staff_managed: 'auth:errors.staffManaged',
  too_many: 'auth:errors.tooManyCodeAttempts',
  user_save_failed: 'auth:errors.userSaveFailed',
  weak_password: 'auth:errors.weakPassword',
};

/** The shape every native auth endpoint's failure response shares. */
export interface AuthErrorLike {
  error?: string;
  message?: string;
}

/**
 * Best available message for a failed auth call, in the active language.
 *
 * Resolution order: our translation for a known code → the server's own
 * message → `fallbackKey`, which callers set to whatever the operation was
 * ("could not create the account", "could not send a reset code").
 */
export function authErrorMessage(res: AuthErrorLike | null | undefined, fallbackKey: string): string {
  const key = res?.error ? ERROR_KEYS[res.error] : undefined;
  if (key) return tr(key);
  if (res?.message) return res.message;
  return tr(fallbackKey);
}

/** Message for a request that never reached the server (fetch threw). */
export const networkErrorMessage = (): string => tr('auth:errors.network');
